import { privacyPolicyData, termsConditionsData } from "@/mocks/homeContent";
import PrivacyPolicyModal from "@/pages/home/components/PrivacyPolicyModal";
import TermsConditionsModal from "@/pages/home/components/TermsConditionsModal";
import { useEffect, useRef, useState } from "react";

const footerLinks = {
  services: [
    { label: "Managed IT", href: "#solutions" },
    { label: "Cybersecurity", href: "#solutions" },
    { label: "Cloud Infrastructure", href: "#solutions" },
    { label: "Custom Websites", href: "#solutions" },
    { label: "SaaS Development", href: "#solutions" },
    { label: "Workflow Automation", href: "#solutions" },
    { label: "VoIP & Communications", href: "#solutions" },
  ],
  company: [
    { label: "About Us", href: "#about" },
    { label: "Why Cybi", href: "#value-props" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Contact", href: "#cta" },
    // { label: "Careers", href: "#" },
  ],
};

export default function Footer() {
  const footerRef = useRef<HTMLElement>(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = el.querySelectorAll(".reveal");
            reveals.forEach((r) => r.classList.add("visible"));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={footerRef}
      className="relative bg-primary-950 text-white overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,oklch(var(--accent-500)/0.06),transparent_70%)] translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,oklch(var(--primary-500)/0.05),transparent_70%)] -translate-x-1/3 translate-y-1/3" />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-6 lg:px-12 pt-20 md:pt-28 pb-8">
        {/* Top row */}
        <div className="reveal pb-14 border-b border-white/8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            {/* Logo + tagline */}
            <div className="max-w-md">
              <a
                href="#hero"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector("#hero")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-block cursor-pointer"
              >
                <span className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Cybi<span className="text-accent-500">.</span>Solutions
                </span>
              </a>
              <p className="mt-4 text-sm text-white/60 leading-relaxed">
                Building the technology behind modern businesses. Managed IT,
                cybersecurity, custom software, and SaaS engineering for
                organizations that demand more.
              </p>
            </div>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 pt-14">
          {/* Services */}
          <div className="reveal reveal-delay-1">
            <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors duration-200 group cursor-pointer"
                  >
                    <span className="w-0 h-px bg-accent-500/60 group-hover:w-3 transition-all duration-200 flex-shrink-0" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="reveal reveal-delay-2">
            <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors duration-200 group cursor-pointer"
                  >
                    <span className="w-0 h-px bg-accent-500/60 group-hover:w-3 transition-all duration-200 flex-shrink-0" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="reveal reveal-delay-3">
            <h4 className="text-xs font-semibold text-white uppercase tracking-wider mb-5">
              Get in Touch
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="https://maps.google.com/?q=123+Innovation+Drive+San+Francisco+CA+94105"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-white/10 transition-colors duration-200">
                    <i className="ri-map-pin-line text-white/60 text-sm w-4 h-4 flex items-center justify-center" />
                  </div>
                  <span className="text-sm text-white/60 group-hover:text-white transition-colors duration-200 leading-relaxed">
                    Miami, Florida
                    <br />
                    {/* Suite 400, San Francisco
                    <br />
                    CA 94105 */}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+7865056326"
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-white/10 transition-colors duration-200">
                    <i className="ri-phone-line text-white/60 text-sm w-4 h-4 flex items-center justify-center" />
                  </div>
                  <span className="text-sm text-white/60 group-hover:text-white transition-colors duration-200">
                    +1 (786) 505-6326
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:hello@cybisolutions.com"
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-white/10 transition-colors duration-200">
                    <i className="ri-mail-line text-white/60 text-sm w-4 h-4 flex items-center justify-center" />
                  </div>
                  <span className="text-sm text-white/60 group-hover:text-white transition-colors duration-200">
                    info@cybisolutions.com
                  </span>
                </a>
              </li>
            </ul>

            {/* CTA button */}
            <a
              href="#cta"
              onClick={(e) => {
                e.preventDefault();
                document
                  .querySelector("#cta")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/70 hover:bg-white/10 hover:text-white hover:border-white/20 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Free Consultation
              <i className="ri-arrow-right-up-line text-sm w-4 h-4 flex items-center justify-center" />
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="reveal reveal-delay-4 mt-16 pt-7 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/60">
            &copy; 2026 Cybi Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowPrivacy(true);
              }}
              className="text-xs text-white/60 hover:text-white transition-colors duration-200 cursor-pointer"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowTerms(true);
              }}
              className="text-xs text-white/60 hover:text-white transition-colors duration-200 cursor-pointer"
            >
              Terms of Service
            </a>
            {/* <a
              href="#"
              className="text-xs text-white/60 hover:text-white transition-colors duration-200 cursor-pointer"
            >
              Cookie Policy
            </a> */}
          </div>
        </div>
      </div>
      {showPrivacy && (
        <PrivacyPolicyModal
          data={privacyPolicyData}
          onClose={() => setShowPrivacy(false)}
        />
      )}

      {showTerms && (
        <TermsConditionsModal
          data={termsConditionsData}
          onClose={() => setShowTerms(false)}
        />
      )}
    </footer>
  );
}
